import { type IngredientWithPortion } from "@fitness-app/data-models/entities/Ingredient";
import {
  MealIngredientStatus,
  type DishInMeal,
  type NutritionPlanDay,
} from "@fitness-app/data-models/entities/MealsPlan";

const isTouchedDish = (dish: DishInMeal | IngredientWithPortion) =>
  Boolean(
    (dish.status && dish.status !== MealIngredientStatus.EMPTY) ||
      dish.fromTracker ||
      (dish.clientNote && dish.clientNote.trim()),
  );

/**
 * Did the client log anything into this day — ate or skipped something, added a dish from the
 * tracker, left a note or a day comment?
 *
 * This answers only half of "is the day worth keeping". A day she rearranged without logging
 * anything (a week prepared in advance by swapping dishes) has every dish at `status: EMPTY` and
 * looks untouched here, which is how the plan-swap cleanup came to delete such weeks (TRE-2033).
 * The cleanup pairs this with `isUntouchedCopyOfPlanDay`, and deletes only a day that is both
 * unlogged AND still a verbatim copy of the plan day it was snapshotted from.
 */
export const hasClientInputInNutritionDay = (day: NutritionPlanDay | null | undefined): boolean => {
  if (!day) {
    return false;
  }

  if (day.comment?.trim()) {
    return true;
  }

  return (day.meals || []).some((meal) => (meal.dishes || []).some(isTouchedDish));
};
